import apiClient from './apiClient';

export const PREFERENCE_AREA = {
  UI: 'ui',
  DASHBOARD: 'dashboard',
  REPORT: 'report',
};

export const SHARE_SCOPE = {
  COMPANY: 'company',
  USER: 'user',
};

/** AppsMediaReports 公司共用剔除圖片（保留，向後相容） */
export const APPS_MEDIA_EXCLUDED = {
  preferenceArea: PREFERENCE_AREA.REPORT,
  module: 'appsMedia',
  settingKey: 'excludedMedia',
  scope: SHARE_SCOPE.COMPANY,
  contextKey: '',
};

/** AppsMediaReports 全公司勾選+排序（per workflowName，所有用戶共享） */
export const APPS_MEDIA_SELECTION = {
  preferenceArea: PREFERENCE_AREA.REPORT,
  module: 'appsMedia',
  settingKey: 'mediaSelection',
  scope: SHARE_SCOPE.COMPANY,
  // contextKey 由呼叫端帶入 workflowName
};

/** AppsMediaReports 個人查詢條件 + PV 分組設定 */
export const APPS_MEDIA_QUERY_STATE = {
  preferenceArea: PREFERENCE_AREA.REPORT,
  module: 'appsMedia',
  settingKey: 'queryState',
  scope: SHARE_SCOPE.USER,
  contextKey: '',
};

/** AppsMediaReports 個人畫廊顯示設定 */
export const APPS_MEDIA_GALLERY_SETTINGS = {
  preferenceArea: PREFERENCE_AREA.REPORT,
  module: 'appsMedia',
  settingKey: 'gallerySettings',
  scope: SHARE_SCOPE.USER,
  contextKey: '',
};

/** TemplateReportBuilder 個人查詢條件 + PV 分組設定 */
export const TEMPLATE_REPORT_QUERY_STATE = {
  preferenceArea: PREFERENCE_AREA.REPORT,
  module: 'templateReport',
  settingKey: 'queryState',
  scope: SHARE_SCOPE.USER,
  contextKey: '',
};

/** AppsMediaReports 全公司「不列印」標記（per workflowName，所有用戶共享） */
export const APPS_MEDIA_USER_EXCLUDED = {
  preferenceArea: PREFERENCE_AREA.REPORT,
  module: 'appsMedia',
  settingKey: 'userExcluded',
  scope: SHARE_SCOPE.COMPANY,
  // contextKey 由呼叫端帶入 workflowName
};

/** TemplateReportBuilder 全公司勾選+排序（per workflowName，所有用戶共享） */
export const TEMPLATE_REPORT_SELECTION = {
  preferenceArea: PREFERENCE_AREA.REPORT,
  module: 'templateReport',
  settingKey: 'mediaSelection',
  scope: SHARE_SCOPE.COMPANY,
  // contextKey 由呼叫端帶入 workflowName
};

/** TemplateReportBuilder 全公司「不列印」標記（per workflowName，所有用戶共享） */
export const TEMPLATE_REPORT_EXCLUDED = {
  preferenceArea: PREFERENCE_AREA.REPORT,
  module: 'templateReport',
  settingKey: 'userExcluded',
  scope: SHARE_SCOPE.COMPANY,
  // contextKey 由呼叫端帶入 workflowName
};

/** TemplateReportBuilder 個人畫廊顯示設定（hideSelected / hideExcluded 等） */
export const TEMPLATE_REPORT_GALLERY_SETTINGS = {
  preferenceArea: PREFERENCE_AREA.REPORT,
  module: 'templateReport',
  settingKey: 'gallerySettings',
  scope: SHARE_SCOPE.USER,
  contextKey: '',
};

/**
 * 正規化媒體 filePath，與 MediaGallery / pdfExport 的 URL 邏輯一致。
 * @param {string} filePath
 * @returns {string}
 */
export function normalizeMediaFilePath(filePath) {
  if (!filePath) return '';

  if (
    filePath.startsWith('http://') ||
    filePath.startsWith('https://') ||
    filePath.startsWith('data:')
  ) {
    return filePath;
  }

  let normalizedPath = filePath;
  if (!normalizedPath.startsWith('/')) {
    normalizedPath = `/${normalizedPath}`;
  }

  if (normalizedPath.startsWith('/Uploads')) {
    return normalizedPath;
  }

  return `/Uploads${normalizedPath}`;
}

/**
 * @param {object} params
 * @param {string} params.preferenceArea
 * @param {string} params.module
 * @param {string} params.settingKey
 * @param {string} [params.scope]
 * @param {string} [params.contextKey]
 */
export async function getUiPreference({
  preferenceArea,
  module,
  settingKey,
  scope = SHARE_SCOPE.COMPANY,
  contextKey = '',
}) {
  const response = await apiClient.get('/api/ui-preferences', {
    params: {
      area: preferenceArea,
      module,
      settingKey,
      scope,
      contextKey: contextKey ?? '',
    },
  });
  return response.data;
}

/**
 * @param {object} params
 * @param {string} params.preferenceArea
 * @param {string} params.module
 * @param {string} params.settingKey
 * @param {object} params.settingsPayload
 * @param {string} [params.scope]
 * @param {string} [params.contextKey]
 * @param {string} [params.settingsFormat]
 */
export async function saveUiPreference({
  preferenceArea,
  module,
  settingKey,
  settingsPayload,
  scope = SHARE_SCOPE.COMPANY,
  contextKey = '',
  settingsFormat = 'json',
}) {
  const response = await apiClient.put('/api/ui-preferences', {
    preferenceArea,
    module,
    settingKey,
    scope,
    contextKey: contextKey ?? '',
    settingsFormat,
    settingsPayload,
  });
  return response.data;
}

/**
 * 載入 AppsMedia 全公司剔除路徑 Set。
 * @returns {Promise<Set<string>>}
 */
export async function loadAppsMediaExcludedPaths() {
  try {
    const data = await getUiPreference(APPS_MEDIA_EXCLUDED);
    const payload = data?.settingsPayload;
    const paths = payload?.excludedFilePaths;
    if (!Array.isArray(paths)) return new Set();
    return new Set(paths.map((p) => normalizeMediaFilePath(p)).filter(Boolean));
  } catch (error) {
    console.error('[uiPreferenceApi] loadAppsMediaExcludedPaths failed:', error);
    return new Set();
  }
}

/**
 * 儲存 AppsMedia 全公司剔除路徑。
 * @param {Set<string>|string[]} excludedFilePaths
 */
export async function saveAppsMediaExcludedPaths(excludedFilePaths) {
  const paths = Array.from(excludedFilePaths instanceof Set ? excludedFilePaths : excludedFilePaths)
    .map((p) => normalizeMediaFilePath(p))
    .filter(Boolean);

  return saveUiPreference({
    ...APPS_MEDIA_EXCLUDED,
    settingsPayload: {
      version: 1,
      excludedFilePaths: paths,
    },
  });
}

/**
 * 載入個人勾選+排序設定（per workflowName）。
 * @param {string} workflowName
 * @returns {Promise<Object>} { [subGroupKey]: string[] }
 */
export async function loadAppsMediaSelection(workflowName) {
  if (!workflowName) return {};
  try {
    const data = await getUiPreference({
      ...APPS_MEDIA_SELECTION,
      contextKey: workflowName,
    });
    const payload = data?.settingsPayload;
    if (!payload?.selectedByGroup || typeof payload.selectedByGroup !== 'object') return {};
    // 正規化每條路徑
    const result = {};
    for (const [key, paths] of Object.entries(payload.selectedByGroup)) {
      if (Array.isArray(paths)) {
        result[key] = paths.map((p) => normalizeMediaFilePath(p)).filter(Boolean);
      }
    }
    return result;
  } catch (error) {
    console.error('[uiPreferenceApi] loadAppsMediaSelection failed:', error);
    return {};
  }
}

/**
 * 儲存個人勾選+排序設定（per workflowName）。
 * @param {string} workflowName
 * @param {Object} selectedByGroup  { [subGroupKey]: string[] }
 */
export async function saveAppsMediaSelection(workflowName, selectedByGroup) {
  if (!workflowName) return;
  // 正規化路徑再存
  const normalized = {};
  for (const [key, paths] of Object.entries(selectedByGroup || {})) {
    if (Array.isArray(paths) && paths.length > 0) {
      normalized[key] = paths.map((p) => normalizeMediaFilePath(p)).filter(Boolean);
    }
  }
  return saveUiPreference({
    ...APPS_MEDIA_SELECTION,
    contextKey: workflowName,
    settingsPayload: {
      version: 1,
      selectedByGroup: normalized,
    },
  });
}

// ─── AppsMediaReports 查詢條件 ───────────────────────────────────────────────

const _defaultAppsMediaQueryState = () => ({
  filters: { status: 'all', workflowName: '', startDateRange: null },
  pvFilters: [],
  groupingPvs: [],
  groupingEformFields: [],
  showPvFilters: false,
});

/**
 * 從後端載入 AppsMediaReports 個人查詢條件。
 * @returns {Promise<object>}
 */
export async function loadAppsMediaQueryState() {
  try {
    const data = await getUiPreference(APPS_MEDIA_QUERY_STATE);
    const p = data?.settingsPayload;
    if (!p) return _defaultAppsMediaQueryState();
    return {
      filters: {
        status: p.filters?.status || 'all',
        workflowName: typeof p.filters?.workflowName === 'string' ? p.filters.workflowName : '',
        startDateRange:
          Array.isArray(p.filters?.startDateRange) && p.filters.startDateRange.length === 2
            ? p.filters.startDateRange
            : null,
      },
      pvFilters: Array.isArray(p.pvFilters) ? p.pvFilters : [],
      groupingPvs: Array.isArray(p.groupingPvs) ? p.groupingPvs : [],
      groupingEformFields: Array.isArray(p.groupingEformFields) ? p.groupingEformFields : [],
      showPvFilters: !!p.showPvFilters,
    };
  } catch {
    return _defaultAppsMediaQueryState();
  }
}

/**
 * 儲存 AppsMediaReports 個人查詢條件（debounce 由呼叫端負責）。
 */
export async function saveAppsMediaQueryState({ filters, pvFilters, groupingPvs, groupingEformFields, showPvFilters }) {
  return saveUiPreference({
    ...APPS_MEDIA_QUERY_STATE,
    settingsPayload: { version: 1, filters, pvFilters, groupingPvs, groupingEformFields, showPvFilters },
  });
}

// ─── AppsMediaReports 畫廊設定 ───────────────────────────────────────────────

const _defaultGallerySettings = () => ({
  maxImagesPerPage: 4,
  imageGap: 5,
  minImageWidth: 50,
  pdfOrientation: 'landscape',
  imageQuality: 0.9,
  hideSelected: true,
  hideExcluded: true,
});

/**
 * 從後端載入 AppsMediaReports 個人畫廊設定。
 * @returns {Promise<object>}
 */
export async function loadAppsMediaGallerySettings() {
  try {
    const data = await getUiPreference(APPS_MEDIA_GALLERY_SETTINGS);
    const p = data?.settingsPayload;
    if (!p) return _defaultGallerySettings();
    return { ..._defaultGallerySettings(), ...p };
  } catch {
    return _defaultGallerySettings();
  }
}

/**
 * 儲存 AppsMediaReports 個人畫廊設定。
 */
export async function saveAppsMediaGallerySettings(settings) {
  return saveUiPreference({
    ...APPS_MEDIA_GALLERY_SETTINGS,
    settingsPayload: { version: 1, ...settings },
  });
}

// ─── TemplateReportBuilder 查詢條件 ──────────────────────────────────────────

const _defaultTemplateReportQueryState = () => ({
  filters: { status: 'all', workflowName: '', startDateRange: null },
  pvFilters: [],
  groupingPvs: [],
  groupingEformFields: [],
  showPvFilters: false,
});

/**
 * 從後端載入 TemplateReportBuilder 個人查詢條件。
 * @returns {Promise<object>}
 */
export async function loadTemplateReportQueryState() {
  try {
    const data = await getUiPreference(TEMPLATE_REPORT_QUERY_STATE);
    const p = data?.settingsPayload;
    if (!p) return _defaultTemplateReportQueryState();
    return {
      filters: {
        status: p.filters?.status || 'all',
        workflowName: typeof p.filters?.workflowName === 'string' ? p.filters.workflowName : '',
        startDateRange:
          Array.isArray(p.filters?.startDateRange) && p.filters.startDateRange.length === 2
            ? p.filters.startDateRange
            : null,
      },
      pvFilters: Array.isArray(p.pvFilters) ? p.pvFilters : [],
      groupingPvs: Array.isArray(p.groupingPvs) ? p.groupingPvs : [],
      groupingEformFields: Array.isArray(p.groupingEformFields) ? p.groupingEformFields : [],
      showPvFilters: !!p.showPvFilters,
    };
  } catch {
    return _defaultTemplateReportQueryState();
  }
}

/**
 * 儲存 TemplateReportBuilder 個人查詢條件（debounce 由呼叫端負責）。
 */
export async function saveTemplateReportQueryState({ filters, pvFilters, groupingPvs, groupingEformFields, showPvFilters }) {
  return saveUiPreference({
    ...TEMPLATE_REPORT_QUERY_STATE,
    settingsPayload: { version: 1, filters, pvFilters, groupingPvs, groupingEformFields, showPvFilters },
  });
}

// ─── AppsMediaReports 個人「不列印」標記 ─────────────────────────────────────

/**
 * 載入個人「不列印」標記（per workflowName）。
 * @param {string} workflowName
 * @returns {Promise<Object>} { [subGroupKey]: string[] }
 */
export async function loadAppsMediaUserExcluded(workflowName) {
  if (!workflowName) return {};
  try {
    const data = await getUiPreference({ ...APPS_MEDIA_USER_EXCLUDED, contextKey: workflowName });
    const payload = data?.settingsPayload;
    if (!payload?.excludedByGroup || typeof payload.excludedByGroup !== 'object') return {};
    const result = {};
    for (const [key, paths] of Object.entries(payload.excludedByGroup)) {
      if (Array.isArray(paths)) {
        result[key] = paths.map((p) => normalizeMediaFilePath(p)).filter(Boolean);
      }
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * 儲存個人「不列印」標記（per workflowName）。
 * @param {string} workflowName
 * @param {Object} excludedByGroup  { [subGroupKey]: string[] }
 */
export async function saveAppsMediaUserExcluded(workflowName, excludedByGroup) {
  if (!workflowName) return;
  const normalized = {};
  for (const [key, paths] of Object.entries(excludedByGroup || {})) {
    if (Array.isArray(paths) && paths.length > 0) {
      normalized[key] = paths.map((p) => normalizeMediaFilePath(p)).filter(Boolean);
    }
  }
  return saveUiPreference({
    ...APPS_MEDIA_USER_EXCLUDED,
    contextKey: workflowName,
    settingsPayload: { version: 1, excludedByGroup: normalized },
  });
}

// ─── TemplateReportBuilder 勾選 + 不列印標記（全公司共享）────────────────────

/**
 * 載入 TemplateReportBuilder 全公司勾選+排序設定（per workflowName）。
 * @param {string} workflowName
 * @returns {Promise<Object>} { [subGroupKey]: string[] }
 */
export async function loadTemplateReportSelection(workflowName) {
  if (!workflowName) return {};
  try {
    const data = await getUiPreference({ ...TEMPLATE_REPORT_SELECTION, contextKey: workflowName });
    const payload = data?.settingsPayload;
    if (!payload?.selectedByGroup || typeof payload.selectedByGroup !== 'object') return {};
    const result = {};
    for (const [key, paths] of Object.entries(payload.selectedByGroup)) {
      if (Array.isArray(paths)) {
        result[key] = paths.map((p) => normalizeMediaFilePath(p)).filter(Boolean);
      }
    }
    return result;
  } catch (error) {
    console.error('[uiPreferenceApi] loadTemplateReportSelection failed:', error);
    return {};
  }
}

/**
 * 儲存 TemplateReportBuilder 全公司勾選+排序設定（per workflowName）。
 * @param {string} workflowName
 * @param {Object} selectedByGroup  { [subGroupKey]: string[] }
 */
export async function saveTemplateReportSelection(workflowName, selectedByGroup) {
  if (!workflowName) return;
  const normalized = {};
  for (const [key, paths] of Object.entries(selectedByGroup || {})) {
    if (Array.isArray(paths) && paths.length > 0) {
      normalized[key] = paths.map((p) => normalizeMediaFilePath(p)).filter(Boolean);
    }
  }
  return saveUiPreference({
    ...TEMPLATE_REPORT_SELECTION,
    contextKey: workflowName,
    settingsPayload: { version: 1, selectedByGroup: normalized },
  });
}

/**
 * 載入 TemplateReportBuilder 全公司「不列印」標記（per workflowName）。
 * @param {string} workflowName
 * @returns {Promise<Object>} { [subGroupKey]: string[] }
 */
export async function loadTemplateReportExcluded(workflowName) {
  if (!workflowName) return {};
  try {
    const data = await getUiPreference({ ...TEMPLATE_REPORT_EXCLUDED, contextKey: workflowName });
    const payload = data?.settingsPayload;
    if (!payload?.excludedByGroup || typeof payload.excludedByGroup !== 'object') return {};
    const result = {};
    for (const [key, paths] of Object.entries(payload.excludedByGroup)) {
      if (Array.isArray(paths)) {
        result[key] = paths.map((p) => normalizeMediaFilePath(p)).filter(Boolean);
      }
    }
    return result;
  } catch (error) {
    console.error('[uiPreferenceApi] loadTemplateReportExcluded failed:', error);
    return {};
  }
}

/**
 * 儲存 TemplateReportBuilder 全公司「不列印」標記（per workflowName）。
 * @param {string} workflowName
 * @param {Object} excludedByGroup  { [subGroupKey]: string[] }
 */
export async function saveTemplateReportExcluded(workflowName, excludedByGroup) {
  if (!workflowName) return;
  const normalized = {};
  for (const [key, paths] of Object.entries(excludedByGroup || {})) {
    if (Array.isArray(paths) && paths.length > 0) {
      normalized[key] = paths.map((p) => normalizeMediaFilePath(p)).filter(Boolean);
    }
  }
  return saveUiPreference({
    ...TEMPLATE_REPORT_EXCLUDED,
    contextKey: workflowName,
    settingsPayload: { version: 1, excludedByGroup: normalized },
  });
}

// ─── TemplateReportBuilder 個人畫廊顯示設定 ──────────────────────────────────

const _defaultTemplateReportGallerySettings = () => ({
  hideSelected: true,
  hideExcluded: false, // 預設顯示所有圖片（含未選取的不列印圖片），讓用戶從中勾選
});

/**
 * 載入 TemplateReportBuilder 個人畫廊顯示設定。
 * @returns {Promise<object>}
 */
export async function loadTemplateReportGallerySettings() {
  try {
    const data = await getUiPreference(TEMPLATE_REPORT_GALLERY_SETTINGS);
    const p = data?.settingsPayload;
    if (!p) return _defaultTemplateReportGallerySettings();
    return { ..._defaultTemplateReportGallerySettings(), ...p };
  } catch {
    return _defaultTemplateReportGallerySettings();
  }
}

/**
 * 儲存 TemplateReportBuilder 個人畫廊顯示設定。
 * @param {object} settings
 */
export async function saveTemplateReportGallerySettings(settings) {
  return saveUiPreference({
    ...TEMPLATE_REPORT_GALLERY_SETTINGS,
    settingsPayload: { version: 1, ...settings },
  });
}
